// Answers to the operator's custom pre-check-in fields.
//
// The ten standard fields are stored on the guest's user and booking, and the tag-category
// questions as tags. Every other field of the form is a CUSTOM field: its answer is read from
// the participant's `precheckin_answers` ({field id: value}, on the booking for the lead booker
// and on each `party[]` entry) and saved in the check-in request's top-level `answers` object,
// never inside `user` (backend: demo/lib/precheckin/answers.py, apiv2/views/bookingmgmt/checkin.py).

// The backend's standard ids (demo/lib/precheckin/fields.py STANDARD_FIELD_TYPES).
export const STANDARD_FIELD_IDS = new Set([
    'first_name',
    'last_name',
    'email',
    'is_minor',
    'phone',
    'custom_note',
    'date_of_birth',
    'passport_number',
    'address',
    'emergency_contact',
]);

const QUESTION_TYPES = new Set(['tag_category', 'category']);
const ANSWER_TYPES = ['string', 'text', 'number', 'date', 'boolean'];

export const isCustomField = (field) => (
    !!field
    && typeof field.id === 'string'
    && field.id !== ''
    && !STANDARD_FIELD_IDS.has(field.id)
    && !QUESTION_TYPES.has(field.type)
);

// A type outside the five reads as `string`, as on the backend.
export const answerType = (field) => (ANSWER_TYPES.includes(field?.type) ? field.type : 'string');

export const getParticipantAnswers = (participant) => {
    const answers = participant?.precheckin_answers;
    return answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {};
};

export const isAnswerEmpty = (value) => (
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
);

// Stored answer -> form value: a boolean for yes/no fields (unanswered = unticked), else a string.
export const answerToFormValue = (field, value) => {
    if (answerType(field) === 'boolean') return value === true;
    return isAnswerEmpty(value) ? '' : String(value);
};

// Form value -> the value sent in `answers`: '' clears the answer, numbers go as JSON numbers.
export const formValueToAnswer = (field, value) => {
    const type = answerType(field);
    if (type === 'boolean') return value === true;
    if (isAnswerEmpty(value)) return '';
    const text = String(value).trim();
    if (type === 'number') {
        const number = Number(text);
        return Number.isFinite(number) ? number : text;
    }
    return text;
};

// Whether a form value is the answer already stored.
export const isSameAnswer = (field, formValue, storedValue) => (
    formValueToAnswer(field, formValue) === formValueToAnswer(field, answerToFormValue(field, storedValue))
);

// The backend's checks of a submitted answer (demo/lib/precheckin/answers.py), with its messages.
export const ANSWER_LIMITS = {
    stringLength: 255,
    textLength: 2000,
    numberDigits: 15,
    dateMin: '1900-01-01',
    dateMax: '2100-12-31',
};

export const ANSWER_MESSAGES = {
    stringTooLong: 'Too long (255 characters at most).',
    textTooLong: 'Too long (2,000 characters at most).',
    number: 'Enter a number (at most 15 digits).',
    date: 'Enter a date as YYYY-MM-DD, from 1900-01-01 to 2100-12-31.',
};

const NUMBER_RE = /^-?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/;
const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

// Digits of a plain decimal, without sign, point, leading zeros and trailing fraction zeros:
// 100 -> 3, 0.0015 -> 2, 1.50 -> 2 (as the backend counts them).
const countDigits = (text) => {
    const [whole, fraction = ''] = text.replace(/^-/, '').split('.');
    return `${whole}${fraction.replace(/0+$/, '')}`.replace(/^0+/, '').length;
};

const isCalendarDate = (text) => {
    if (!DATE_RE.test(text)) return false;
    const [year, month, day] = text.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

// The message for a value the backend would refuse, or null. Empty values clear and pass.
export const checkAnswerValue = (field, value) => {
    const type = answerType(field);
    if (type === 'boolean' || isAnswerEmpty(value)) return null;
    const text = String(value).trim();
    switch (type) {
        case 'text':
            // Characters, not UTF-16 units: the backend counts code points
            return [...text].length > ANSWER_LIMITS.textLength ? ANSWER_MESSAGES.textTooLong : null;
        case 'number':
            return NUMBER_RE.test(text) && countDigits(text) <= ANSWER_LIMITS.numberDigits
                ? null
                : ANSWER_MESSAGES.number;
        case 'date':
            return isCalendarDate(text) && text >= ANSWER_LIMITS.dateMin && text <= ANSWER_LIMITS.dateMax
                ? null
                : ANSWER_MESSAGES.date;
        default:
            return [...text].length > ANSWER_LIMITS.stringLength ? ANSWER_MESSAGES.stringTooLong : null;
    }
};

// A message in the form's field-error style: "* Message" (as "* Required").
export const toFieldErrorText = (message) => {
    const text = String(message || '').trim().replace(/\.$/, '');
    return text.startsWith('*') ? text : `* ${text}`;
};

// The per-field messages of a 400 body `{error, errors: {field id: message}}`, or null.
export const fieldErrorsFromResponse = (body) => {
    const errors = body?.errors;
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;
    const out = {};
    Object.entries(errors).forEach(([id, message]) => {
        if (typeof message === 'string' && message.trim()) out[id] = message;
    });
    return Object.keys(out).length ? out : null;
};

// The backend's write, for optimistic updates: '' / null clears an id, any other value replaces
// it, ids not submitted keep their value.
export const mergeAnswers = (stored, submitted) => {
    const next = { ...getParticipantAnswers({ precheckin_answers: stored }) };
    Object.entries(submitted || {}).forEach(([id, value]) => {
        if (isAnswerEmpty(value)) {
            delete next[id];
        } else {
            next[id] = typeof value === 'string' ? value.trim() : value;
        }
    });
    return next;
};
